import { useEffect, useRef, useState } from "react";
import { useUser } from "../../store/auth/AuthSelectors";
import useBaseRequest from "../../api/BaseRequest";
import DigitalSignatureQualifiedGateway from "../../api/gateways/DigitalSignatureQualifiedGateway";
import { IQdsRequest } from "../../entities/IQdsRequest";

export interface SigningOrder {
  canSign: boolean;
  isUserTurn: boolean;
  turnEmail: string | null;
  userPrio: number | null;
}

export interface NextSigner {
  email: string | null;
  prio: number | null;
  type: "counterparty" | "holder" | null;
}

/**
 * Determines who is next to sign (first pending counterparty, then holder)
 * This is independent of current user - shows who should sign next
 */
function getNextSigner(qdsRequest: IQdsRequest | null): NextSigner {
  if (!qdsRequest) {
    return {
      email: null,
      prio: null,
      type: null,
    };
  }

  // Sort counter party signatures by priority
  const sortedCounterParty = [...(qdsRequest.counterPartySignatures || [])].sort(
    (a, b) => (a.prio || 0) - (b.prio || 0)
  );

  // Find first counter party that is pending
  const pendingCounterParty = sortedCounterParty.find(
    (sig) => sig.status?.toLowerCase() === "pending"
  );

  if (pendingCounterParty) {
    return {
      email: pendingCounterParty.email,
      prio: pendingCounterParty.prio || null,
      type: "counterparty",
    };
  }

  // If all counter party have action taken, check holder signatures
  const sortedHolder = [...(qdsRequest.holderSignatures || [])].sort(
    (a, b) => (a.prio || 0) - (b.prio || 0)
  );

  // Find first holder that is pending
  const pendingHolder = sortedHolder.find(
    (sig) => sig.status?.toLowerCase() === "pending"
  );

  if (pendingHolder) {
    return {
      email: pendingHolder.email,
      prio: pendingHolder.prio || null,
      type: "holder",
    };
  }

  return {
    email: null,
    prio: null,
    type: null,
  };
}

/**
 * Determines if current user can sign based on priority order
 */
function getCurrentSigningOrder(
  qdsRequest: IQdsRequest | null,
  currentUserId: number | undefined,
  nextSigner: NextSigner
): SigningOrder {
  if (!qdsRequest || !currentUserId || !nextSigner.email) {
    return {
      canSign: false,
      isUserTurn: false,
      turnEmail: null,
      userPrio: null,
    };
  }

  // Get current user's signature in next signer list
  let currentUserSignature: any = null;

  if (nextSigner.type === "counterparty") {
    currentUserSignature = qdsRequest.counterPartySignatures?.find(
      (sig) => sig.userId === currentUserId && sig.email === nextSigner.email
    );
  } else if (nextSigner.type === "holder") {
    currentUserSignature = qdsRequest.holderSignatures?.find(
      (sig) => sig.userId === currentUserId && sig.email === nextSigner.email
    );
  }

  const isUserTurn = !!currentUserSignature;

  return {
    canSign: isUserTurn,
    isUserTurn,
    turnEmail: nextSigner.email,
    userPrio: nextSigner.prio,
  };
}

export function useQdsSignDetail(qdsRequestId: string) {
  const currentUser = useUser();
  const [loading, setLoading] = useState(true);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Fetch QDS Request with embedded document data
  const { data: qdsData, loading: qdsLoading, execute: refetch } = useBaseRequest(
    DigitalSignatureQualifiedGateway.getQdsRequestById,
    {
      initialPayload: qdsRequestId,
      autoFetch: !!qdsRequestId,
    }
  );

  // Set loading state
  useEffect(() => {
    setLoading(qdsLoading);
  }, [qdsLoading]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Extract document from qdsRequest
  const document = qdsData?.document || null;
  const qdsRequest = qdsData || null;

  // Get next signer regardless of current user
  const nextSigner = getNextSigner(qdsRequest);

  // Get current signing order based on whether current user is next
  const currentUserId = currentUser?.backEndId ? Number(currentUser.backEndId) : undefined;
  const signingOrder = getCurrentSigningOrder(qdsRequest, currentUserId, nextSigner);

  // Also expose nextSigner for displaying who's turn is it
  const nextSignerEmail = nextSigner.email;
  const nextSignerPrio = nextSigner.prio;

  // Always keep ref to latest refetch to avoid stale closures in intervals/channels
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });

  // Poll every 5 seconds as reliable fallback for cross-window status updates
  useEffect(() => {
    if (!qdsRequestId) return;
    const interval = setInterval(() => {
      refetchRef.current(qdsRequestId);
    }, 5000);
    return () => clearInterval(interval);
  }, [qdsRequestId]);

  // Listen for QDS signature updates from other tabs/windows via BroadcastChannel
  useEffect(() => {
    const channel = new BroadcastChannel('qds_signature_update');

    const handleMessage = (event: MessageEvent) => {
      if (event.data.qdsRequestId === parseInt(qdsRequestId)) {
        refetchRef.current(qdsRequestId);
      }
    };

    channel.addEventListener('message', handleMessage);

    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }, [qdsRequestId]);

  return {
    document,
    qdsRequest,
    loading,
    currentUser,
    windowHeight,
    windowWidth,
    refetch: () => refetch(qdsRequestId),
    signingOrder,
    nextSignerEmail,
    nextSignerPrio,
  };
}
